import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
  type ComputePositionConfig,
  type OffsetOptions,
  type Placement,
} from '@floating-ui/dom'
import { Controller } from '@hotwired/stimulus'

export default class DropdownController extends Controller<HTMLElement> {
  static targets = ['arrow', 'button', 'dialog']

  declare cleanup: () => void | null
  declare expanded: boolean
  declare hasArrow: boolean
  declare readonly arrowTarget: HTMLDivElement | null
  declare readonly buttonTarget: HTMLButtonElement
  declare readonly dialogTarget: HTMLDivElement
  declare readonly hasArrowTarget: boolean

  connect() {
    this.cleanup = null
    this.expanded = this.buttonTarget.getAttribute('aria-expanded') === 'true'
    this.hasArrow = this.element.dataset.dropdownArrow === 'true'

    if (this.hasArrow) this.#attachArrow()
    if (this.expanded) this.#autoUpdate()

    document.addEventListener('click', this.#handleClick.bind(this))
    document.addEventListener('keyup', this.#handleKeyup.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.#handleClick.bind(this))
    document.removeEventListener('keyup', this.#handleKeyup.bind(this))
    if (this.cleanup !== null) this.cleanup()
  }

  buttonTargetConnected(element: HTMLButtonElement) {
    element.addEventListener('keyup', this.#handleButtonKeyup.bind(this))
  }

  buttonTargetDisconnected(element: HTMLButtonElement) {
    element.removeEventListener('keyup', this.#handleButtonKeyup.bind(this))
  }

  // Public methods

  collapse() {
    this.buttonTarget.setAttribute('aria-expanded', 'false')
    this.dialogTarget.classList.add('hidden')
    this.expanded = false
    if (this.cleanup !== null) {
      this.cleanup()
    }
    this.#dispatchDatasetEvent('dropdownCollapseEvent')
    this.dispatch('collapse')
  }

  expand() {
    this.buttonTarget.setAttribute('aria-expanded', 'true')
    this.dialogTarget.classList.remove('hidden')
    this.#autoUpdate()
    this.expanded = true
    this.#dispatchDatasetEvent('dropdownExpandEvent')
    this.dispatch('expand')
  }

  toggle() {
    this.expanded ? this.collapse() : this.expand()
    this.#dispatchDatasetEvent('dropdownToggleEvent')
    this.dispatch('toggle')
  }

  toggleFromShortcut(event: Event) {
    // Bail if the target isn't `<body>` (which almost always means that the
    // target is a form control). This might not be perfect, but it should
    // work well enough for now. Long term, we'll probably want to create
    // a Stimulus controller to handle keyboard shortcuts at a global app level.
    if (event.target !== document.body) return

    this.toggle()
  }

  // NOTE: This is a kinda hacky method that doesn't seem to belong here.
  // We're shoe-horning some functionality into this controller because it
  // feels cleanest to put it here, but even so...it doesn't feel clean.
  // Keep an eye on this one. If the scope starts to creep, we should rip it out
  // of this controller and put it into a different controller that perhaps
  // references this one in an outlet.
  useInputForToggleText(event: KeyboardEvent | PointerEvent) {
    const toggleTextElement =
      this.buttonTarget.querySelector<HTMLElement>('[data-dropdown-button-text]') || this.buttonTarget

    const {
      dataset: { label, tone },
      value,
    } = event.target as HTMLInputElement

    const toneClasses = {
      positive: ['!text-green-700', 'hover:bg-green-50', 'hover:!text-green-800'],
      negative: ['!text-red-700', 'hover:bg-red-50', 'hover:!text-red-800'],
    }

    const isClickEvent = event instanceof PointerEvent
    const isEnterKeyEvent = event instanceof KeyboardEvent && event.key === 'Enter'

    // NOTE: Due to "reasons," browsers fire simulated click events before
    // firing input events. The browser treats these events as real PointerEvent
    // objects, which is even more frustrating. We want to make a distinction
    // between events that are initiated by a physical pointer and those that
    // are initiated by a "simulated" pointer (e.g. keyboard and input events).
    // Checking for the dimensions of the click is hacky but possibly the only
    // method of doing so. (Simulated clicks return `0` for these values.)
    const isRealClickEvent = isClickEvent && event.clientX !== 0

    if (!label || !value) return

    if (isEnterKeyEvent || isRealClickEvent) {
      toggleTextElement.dataset.originalText = toggleTextElement.innerText
      toggleTextElement.innerText = label
      this.buttonTarget.classList.remove(...Object.values(toneClasses).flat())

      if (tone) this.buttonTarget.classList.add(...toneClasses[tone])

      this.collapse()

      // Stop the enter key from submitting the form while expanded
      if (isEnterKeyEvent) event.preventDefault()
    }
  }

  // Private methods

  #attachArrow() {
    const arrow = document.createElement('div')
    arrow.setAttribute(
      'class',
      `
      absolute rotate-45 size-4 group-[.default-theme]:bg-white
    `,
    )
    arrow.id = `${this.dialogTarget.id}-arrow`
    arrow.dataset.dropdownTarget = 'arrow'

    this.dialogTarget.append(arrow)
  }

  #autoUpdate() {
    this.cleanup = autoUpdate(this.buttonTarget, this.dialogTarget, this.#computePosition.bind(this))
  }

  #clickedWithin(referenceElement: HTMLElement, clickedElement: HTMLElement) {
    return referenceElement === clickedElement || referenceElement.contains(clickedElement)
  }

  #computePosition() {
    const config = {
      middleware: this.#getMiddleware(),
      placement: this.#getPlacement(),
    } satisfies ComputePositionConfig

    computePosition(this.buttonTarget, this.dialogTarget, config).then(this.#handleComputedPosition.bind(this))
  }

  #dispatchDatasetEvent(eventName: string) {
    const value = this.element.dataset[eventName]
    if (value) this.dispatch(value, { prefix: '' })
  }

  #getMiddleware() {
    const { dropdownOffset } = this.element.dataset
    let offsetOptions: OffsetOptions = dropdownOffset ? JSON.parse(dropdownOffset) : 0

    if (this.hasArrow && typeof offsetOptions === 'number') {
      offsetOptions += 8 // Add arrow size to offset
    }

    const middleware: ComputePositionConfig['middleware'] = [offset(offsetOptions), flip(), shift({ padding: 8 })]

    middleware.push(
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          })
        },
      }),
    )

    if (this.hasArrow && this.hasArrowTarget) {
      middleware.push(arrow({ element: this.arrowTarget }))
    }

    return middleware
  }

  #getPlacement() {
    const { dropdownPlacement } = this.element.dataset
    return (dropdownPlacement as Placement) || 'bottom'
  }

  #handleButtonKeyup(event: KeyboardEvent) {
    if (!this.expanded) return

    const { key } = event
    const isNextKey = ['ArrowDown', 'ArrowRight'].includes(key)
    const isPrevKey = ['ArrowLeft', 'ArrowUp'].includes(key)
    const inputs = this.dialogTarget.querySelectorAll('input')

    if ((!isNextKey && !isPrevKey) || inputs.length === 0) return

    if (isNextKey) {
      const firstInput = inputs[0] as HTMLInputElement
      firstInput.focus()
    } else if (isPrevKey) {
      const lastInput = inputs[inputs.length - 1] as HTMLInputElement
      lastInput.focus()
    }
  }

  #handleClick(event: Event) {
    const target = event.target as HTMLElement
    const clickedWithinButton = this.#clickedWithin(this.buttonTarget, target)
    const clickedWithinDialog = this.#clickedWithin(this.dialogTarget, target)

    if (clickedWithinButton) {
      this.toggle()
    } else if (clickedWithinDialog) {
      // Intentionally do nothing
    } else if (this.expanded) {
      this.collapse()
    }
  }

  #handleComputedPosition({ middlewareData, placement, x, y }) {
    Object.assign(this.dialogTarget.style, {
      left: `${x}px`,
      top: `${y}px`,
    })

    if (!this.hasArrow || !this.hasArrowTarget) return

    const { x: arrowX, y: arrowY } = middlewareData.arrow

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]]

    Object.assign(this.arrowTarget.style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: '-8px',
    })
  }

  #handleKeyup(event: KeyboardEvent) {
    const collapseKeys = ['Escape']

    if (collapseKeys.includes(event.key)) this.collapse()
  }
}
